/**
 * License & Features Hook
 * Phase 2.1: Client-side license verification and feature gating
 * Builds on Phase 1 collections architecture
 */
import { useState, useEffect } from 'react';
// Phase 2.6: Temporarily disabled for System B removal - will be restored in Phase 3
// import { useVesselCollections } from './useVesselCollections';
import { createBaseRecord } from '../types/core';

// Feature flags for different license tiers
export const FEATURES = {
  // Free tier
  BASIC_COMPLIANCE: 'basic-compliance',
  SINGLE_VESSEL: 'single-vessel',

  // Pro tier
  MULTI_VESSEL: 'multi-vessel',
  ADVANCED_REPORTS: 'advanced-reports',
  CUSTOM_PROCEDURES: 'custom-procedures',
  CLOUD_SYNC: 'cloud-sync',

  // Fleet tier
  FLEET_MANAGEMENT: 'fleet-management',
  USER_MANAGEMENT: 'user-management',
  AUDIT_TRAIL: 'audit-trail',
  API_ACCESS: 'api-access',

  // FT-18: Facility Management (marina, building, workshop, etc.)
  // NOTE: Temporarily in free tier for Phase 2.6 trial — move to pro/fleet when license system re-enabled
  FACILITY_MANAGEMENT: 'facility-management'
};

// License plans and their included features
export const LICENSE_PLANS = {
  free: {
    name: 'Free',
    // FT-18: FACILITY_MANAGEMENT temporarily in free tier for Phase 2.6 trial
    // TODO: Move to pro/fleet tier when license system is re-enabled post-migration
    features: [FEATURES.BASIC_COMPLIANCE, FEATURES.SINGLE_VESSEL, FEATURES.FACILITY_MANAGEMENT],
    maxVessels: 1,
    maxUsers: 1
  },
  pro: {
    name: 'Professional',
    features: [
      FEATURES.BASIC_COMPLIANCE,
      FEATURES.SINGLE_VESSEL,
      FEATURES.MULTI_VESSEL,
      FEATURES.ADVANCED_REPORTS,
      FEATURES.CUSTOM_PROCEDURES,
      FEATURES.CLOUD_SYNC
    ],
    maxVessels: 10,
    maxUsers: 5
  },
  fleet: {
    name: 'Fleet',
    features: Object.values(FEATURES),
    maxVessels: -1, // unlimited
    maxUsers: 50
  }
};

export const useLicense = () => {
  // Phase 2.6: Collections disabled - will restore in Phase 3
  // const { collections } = useVesselCollections();
  const [license, setLicense] = useState(null);
  const [org, setOrg] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Phase 2.6: License loading temporarily disabled - defaults to free plan
  // Load license data from collections
  useEffect(() => {
    const loadLicense = async () => {
      try {
        // Phase 2.6: Default to free plan (collections disabled)
        console.warn('Phase 2.6: License loading disabled - defaulting to free plan');
        setFeatures(LICENSE_PLANS.free.features);

        // // Check for stored license data
        // const licenseRecord = await collections.get('meta', 'license_data');
        // const orgRecord = await collections.get('meta', 'org_data');
        //
        // if (licenseRecord && orgRecord) {
        //   setLicense(licenseRecord.data);
        //   setOrg(orgRecord.data);
        //
        //   // Get features for current plan
        //   const plan = licenseRecord.data.plan || 'free';
        //   const planFeatures = LICENSE_PLANS[plan]?.features || LICENSE_PLANS.free.features;
        //   setFeatures(planFeatures);
        // } else {
        //   // Default to free plan
        //   setFeatures(LICENSE_PLANS.free.features);
        // }
      } catch (err) {

        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadLicense();
  }, []);

  // Phase 2.6: License verification temporarily disabled - will restore in Phase 3
  // Verify license key with server
  const verifyLicense = async (licenseKey) => {
    console.warn('Phase 2.6: License verification disabled - will return in Phase 3');
    setLoading(false);
    setError('License verification temporarily unavailable during migration');
    throw new Error('License verification temporarily unavailable');

    // setLoading(true);
    // setError(null);
    //
    // try {
    //   // TODO: Replace with actual API endpoint
    //   const response = await fetch('/api/license/verify', {
    //     method: 'POST',
    //     headers: {
    //       'Content-Type': 'application/json'
    //     },
    //     body: JSON.stringify({
    //       key: licenseKey,
    //       deviceFingerprint: generateDeviceFingerprint()
    //     })
    //   });
    //
    //   if (!response.ok) {
    //     throw new Error('License verification failed');
    //   }
    //
    //   const data = await response.json();
    //   const { org: orgData, plan, seats, expiresAt, features: serverFeatures } = data;
    //
    //   // Store license data in collections
    //   const licenseData = {
    //     key: licenseKey,
    //     plan,
    //     seats,
    //     expiresAt,
    //     verifiedAt: new Date().toISOString(),
    //     features: serverFeatures
    //   };
    //
    //   await collections.upsert('meta', createBaseRecord({
    //     id: 'license_data',
    //     vesselId: collections.vesselId,
    //     data: licenseData,
    //     origin: 'system'
    //   }));
    //
    //   await collections.upsert('meta', createBaseRecord({
    //     id: 'org_data',
    //     vesselId: collections.vesselId,
    //     data: orgData,
    //     origin: 'system'
    //   }));
    //
    //   setLicense(licenseData);
    //   setOrg(orgData);
    //   setFeatures(serverFeatures || LICENSE_PLANS[plan]?.features || []);
    //
    //   return data;
    // } catch (err) {
    //
    //   setError(err.message);
    //   throw err;
    // } finally {
    //   setLoading(false);
    // }
  };

  // Check if a feature is enabled
  const hasFeature = (feature) => {
    return features.includes(feature);
  };

  // Check if within license limits
  const checkLimits = () => {
    if (!license) return { vessels: true, users: true };
    
    const plan = LICENSE_PLANS[license.plan] || LICENSE_PLANS.free;
    
    return {
      vessels: plan.maxVessels === -1 || true, // TODO: Get actual vessel count
      users: plan.maxUsers === -1 || true     // TODO: Get actual user count
    };
  };

  // Get license status
  const getLicenseStatus = () => {
    if (!license) return 'unlicensed';
    
    const now = new Date();
    const expires = new Date(license.expiresAt);
    
    if (expires < now) return 'expired';
    if (expires < new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)) return 'expiring'; // 30 days
    return 'active';
  };

  return {
    license,
    org,
    features,
    loading,
    error,
    verifyLicense,
    hasFeature,
    checkLimits,
    getLicenseStatus,
    isLicensed: !!license,
    plan: license?.plan || 'free',
    FEATURES
  };
};

// Generate a simple device fingerprint
const generateDeviceFingerprint = () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('Device fingerprint', 2, 2);
  
  return btoa(JSON.stringify({
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenResolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    canvas: canvas.toDataURL()
  })).slice(0, 32);
};